import React, { useMemo, useState, useCallback } from "react";
import { Stack, Checkbox, Typography, Collapse } from "@mui/material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import { ROUTING_SECTIONS } from "../../../constants";
import { useGetPartData } from "../../../hooks";

import PartMainDetails from "./PartMainDetails";
import PartRoutingBar from "./PartRoutingBar";
import { SECTION_FORMS } from "../../../tables/RoutingTable/helper";
import RoutingComments from "../../../tables/RoutingTable/RoutingComments";
import RoutingTableSkeleton from "../../../tables/RoutingTable/RoutingTableSkeleton";
import {
  StyledContainer,
  StyledTableContainer,
} from "../../../tables/RoutingTable/styles";

const findDepartmentDetails = (departmentId) =>
  ROUTING_SECTIONS.find((d) => d.departmentId === departmentId);

const orderRank = (departmentId) => {
  const i = ROUTING_SECTIONS.findIndex((d) => d.departmentId === departmentId);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
};

const SectionRow = ({
  section,
  instructionIndex,
  instruction,
  isSelected,
  onToggle,
}) => {
  const Form = SECTION_FORMS[section.departmentId];
  return (
    <Stack
      sx={{
        borderTop: "1px solid",
        borderColor: "hubColors.greyLight",
        py: 1,
      }}
    >
      <Stack direction="row" alignItems="center" gap="10px" sx={{ px: 2 }}>
        <Checkbox
          checked={isSelected}
          onChange={onToggle}
          sx={{ padding: 0, width: 16 }}
        />
        <Typography variant="caption">{section.label}</Typography>
      </Stack>
      {isSelected && Form && (
        <Stack sx={{ mt: 1 }}>
          <Form
            basePath={`instructions.${instructionIndex}`}
            defaults={instruction}
          />
        </Stack>
      )}
    </Stack>
  );
};

const HiddenSections = ({ hidden, instructions, onToggle }) => {
  const [open, setOpen] = useState(false);
  if (!hidden.length) return null;
  return (
    <Stack sx={{ borderTop: "1px solid", borderColor: "hubColors.greyLight" }}>
      <Typography
        onClick={() => setOpen((o) => !o)}
        sx={{ px: 2, py: 1, cursor: "pointer", fontSize: 12 }}
      >
        {open ? "Hide" : "Show Hidden"}
      </Typography>
      <Collapse in={open}>
        {hidden.map((section) => (
          <SectionRow
            key={section.departmentId}
            section={section}
            instructionIndex={-1}
            instruction={null}
            isSelected={false}
            onToggle={() => onToggle(section.departmentId, true)}
          />
        ))}
      </Collapse>
    </Stack>
  );
};

const PartRoutingDetails = ({ value, setValue = () => {} }) => {
  const { id } = useParams();
  const { data: partDetails } = useGetPartData({ id, locationId: value });

  const { setValue: setFormValue } = useFormContext();

  const { fields: instructions, replace } = useFieldArray({
    name: "instructions",
  });

  // Section is "selected" iff an instruction entry exists for its departmentId.
  const selectedIds = useMemo(
    () => new Set(instructions.map((i) => i?.departmentId).filter(Boolean)),
    [instructions],
  );

  const handleToggle = useCallback(
    (departmentId, shouldSelect) => {
      const next = shouldSelect
        ? [
            ...instructions,
            { departmentId, notes: "", config: {}, routing: [{}] },
          ]
        : instructions.filter((i) => i?.departmentId !== departmentId);
      replace(next);
    },
    [instructions, setFormValue],
  );

  const hidden = ROUTING_SECTIONS.filter(
    (s) => !selectedIds.has(s.departmentId),
  );

  if (!partDetails) {
    return (
      <Stack sx={{ height: "calc(100vh - 102px)", overflow: "hidden" }}>
        <PartRoutingBar value={value} setValue={setValue} />
        <RoutingTableSkeleton />
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: "calc(100vh - 102px)", overflow: "hidden" }}>
      <PartRoutingBar value={value} setValue={setValue} />
      <StyledContainer id="RoutingTableContainer">
        <RoutingComments />
        <StyledTableContainer as={Stack}>
          <PartMainDetails locationId={value} quotes={partDetails?.quotes} />
          {instructions
            .map((ins, idx) => ({
              ins,
              idx,
              section: findDepartmentDetails(ins.departmentId),
            }))
            .filter(({ section }) => !!section)
            .sort(
              (a, b) =>
                orderRank(a.ins.departmentId) - orderRank(b.ins.departmentId),
            )
            .map(({ ins, idx, section }) => (
              <SectionRow
                key={section.departmentId}
                section={section}
                instructionIndex={idx}
                instruction={ins}
                isSelected
                onToggle={() => handleToggle(section.departmentId, false)}
              />
            ))}
          <HiddenSections
            hidden={hidden}
            instructions={instructions}
            onToggle={handleToggle}
          />
        </StyledTableContainer>
      </StyledContainer>
    </Stack>
  );
};

export default PartRoutingDetails;
